import { EditModal } from 'components/Modals/EditModal/EditModal';
import { fetchData, IDataObject, saveData } from 'data/DataConnector';
import { useEffect, useRef, useState } from 'react';
import { PageMessageIconType } from 'util/enums/PageMessageIconTypeEnum';
import { ConvertDouble, getTrimmedValue, getUsername, objectToCamel } from 'util/helpers/helperFunctions';
import { useEditBase } from 'views/useEditBase';
import { MethodType } from './DropDown/MethodType';
import GridControl, { IGridEditParams, Theme } from './GridControl';
import { onEditorPreparing } from './GridFunctions';
import { DeleteModal } from './Modals/DeleteModal/DeleteModal';
import PageMessageControl from './PageMessageControl/PageMessageControl';
import TextboxControlRow from './TextboxControl/TextboxControlRow';

interface IProps {
  onClose: any;
  onSave: any;
  parameters: any;
}

const GridLayoutEdit = (props: IProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [modalHeader] = useState('Layout Edit');
  const [dataList, setDataList] = useState<IDataObject>({ schema: [], data: [] });
  const [spName, setSpName] = useState('');
  const [spParameters, setSpParameters] = useState('');
  const [source, setSource] = useState('');
  const [includedLocalStore, setIncludedLocalStore] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const modalRef = useRef<any>(null);
  const gridRef = useRef<any>(null);
  const timeoutRef = useRef<any>(null);
  const editedRowsRef = useRef<any>([]);
  const editedRowsObjRef = useRef<any>({});
  const saveInlineChangesReqRef = useRef<boolean>(true);
  const editParamsRef = useRef<IGridEditParams>({
    columnToDatabaseList: [
      { key: 'Width', value: 'Width' },
      { key: 'Data Type', value: 'Data Type' },
      { key: 'Column Name', value: 'Column Name' },
      { key: 'Hidden', value: 'Hidden' },
    ],
    controllerName: 'crossRef',
    methodName: 'TestMethod',
  });
  const spIdRef = useRef<any>(0);

  const state = {
    isLoading,
    modalHeader,
    dataList,
    spName,
    spParameters,
    source,
    includedLocalStore,
    gridLoading,
    showDelete,
  };
  const { genericChangeHandler } = useEditBase(state, {});

  useEffect(() => {
    const initialize = async () => {
      await initializeData();
      await searchData();
      setIsLoading(false);
    };
    initialize();
  }, []);

  const initializeData = async () => {
    const sqlCommandText = props.parameters.sqlCommandText;
    const spNameVal = sqlCommandText.toLowerCase().split('exec ')[1].split(' ')[0];
    let spParams: string = sqlCommandText.toLowerCase().split('exec ')[1];
    spParams = spParams.substr(spParams.indexOf('@'));
    setSource(sqlCommandText);
    setSpName(spNameVal);
    setSpParameters(spParams);
  };

  const onRowValidating = (event: any) => {
    clearTimeout(timeoutRef.current);
    const rowData = { ...event.oldData, ...event.newData };
    if ((rowData['Lookup SP'] || rowData['Lookup Type']) && !rowData['Lookup Code Field']) {
      event.isValid = false;
      event.errorText = '"Lookup Code Field" is required';
      return;
    }
    editedRowsRef.current.push(rowData);
    const oldData = editedRowsObjRef.current[event.oldData.dataField] || {};
    editedRowsObjRef.current[event.oldData.dataField] = {
      ...oldData,
      ...event.newData,
    };
    if (saveInlineChangesReqRef.current) {
      timeoutRef.current = setTimeout(() => {
        onSave(editedRowsRef.current);
      }, 1000);
    }
  };

  const onReorder = (event: any) => {
    const keyField = 'Column Name';
    let data = [...dataList.data];
    const visibleRows = event.component.getVisibleRows();
    const fromIndexKeyField = visibleRows[event.fromIndex].data[keyField];
    const fromIndexData = data.filter((row: any) => row[keyField] === fromIndexKeyField)[0];
    const toIndexKeyField = visibleRows[event.toIndex].data[keyField];
    const toIndexData = data.filter((row: any) => row[keyField] === toIndexKeyField)[0];
    const toIndex = data.indexOf(toIndexData);
    data = data.filter((row: any) => row[keyField] !== fromIndexKeyField);
    data.splice(toIndex, 0, fromIndexData);
    setDataList({
      ...dataList,
      data,
    });
  };

  const onEditorPreparingHandler = (event: any) => {
    if (event.parentType !== 'filterRow' && event.dataField === 'Lookup SP Parameter' && !event.row.data['Lookup SP']) {
      event.editorOptions.readOnly = true;
    } else {
      onEditorPreparing(event);
    }
  };

  const searchData = async (createSchema: boolean = true) => {
    setGridLoading(true);
    let includedLocalStoreVal = false;
    const localStorageInfoJson = window.localStorage.getItem(props.parameters.storageKey);

    const schemaGetResponse = await fetchData('ExpressionBuilder', 'SpSchemaGet', MethodType.Post, {
      spName: spName,
      spParams: spParameters,
    });
    let spParametersVal = '';
    if (schemaGetResponse.dataObject.data.length > 0) {
      spParametersVal = schemaGetResponse.dataObject.data[0].sp_params;
      spIdRef.current = schemaGetResponse.dataObject.data[0].sp_id;
    }
    let localStorageInfo: any = { columns: [] };

    if (localStorageInfoJson) {
      localStorageInfo = JSON.parse(localStorageInfoJson);
    }

    const columnData: any = {};
    // There are 3 sources of information; LocalStorage, sp_data_dict and schema of the grid.
    // using information from LocalStorage
    localStorageInfo.columns.forEach((colData: any) => {
      if (colData.dataField) {
        columnData[colData.dataField] = {
          ...colData,
          columnOrder: colData.visibleIndex,
        };
      }
    });
    // using information from sp_data_dict
    schemaGetResponse.dataObject.data.forEach((row: any) => {
      columnData[row.column_name] = {
        ...columnData[row.column_name],
        ...objectToCamel(row),
      };
      if (columnData[row.column_name].columnOrder !== columnData[row.column_name].visibleIndex) {
        columnData[row.column_name].columnOrder = columnData[row.column_name].visibleIndex;
      }
    });
    // using information from schema
    props.parameters.schema.forEach((scheme: any) => {
      if (!(scheme.ColumnName in columnData)) {
        columnData[scheme.ColumnName] = {};
      }
      columnData[scheme.ColumnName] = {
        ...columnData[scheme.ColumnName],
        // ...objectToCamel(scheme),
        columnCaption: columnData[scheme.ColumnName].columnCaption || scheme.Caption,
        dataType: columnData[scheme.ColumnName].dataType || scheme.DataTypeName,
        isHidden: columnData[scheme.ColumnName].isHidden || scheme.Hidden,
        isReadonly: columnData[scheme.ColumnName].isReadonly || scheme.IsReadonly,
      };
      if (columnData[scheme.ColumnName].width === scheme.Width) {
        columnData[scheme.ColumnName].width = columnData[scheme.ColumnName].columnWidth
          ? columnData[scheme.ColumnName].columnWidth
          : null;
      } else if (columnData[scheme.ColumnName].width) {
        includedLocalStoreVal = true;
      }
      if (
        getTrimmedValue(scheme.ColumnOrder) !== '' &&
        columnData[scheme.ColumnName].columnOrder !== Number(scheme.ColumnOrder)
      ) {
        includedLocalStoreVal = true;
      }
    });

    let schema: any = [];
    let lookupDataList: any = [];
    if (createSchema) {
      schema.push(getSchema('Column Name', { IsReadOnly: true }));
      schema.push(getSchema('Caption'));
      schema.push(getSchema('Width', { DataTypeName: 'int' }));
      schema.push(getSchema('Data Type', { IsReadOnly: true }));
      schema.push(getSchema('Hidden', { DataTypeName: 'boolean' }));
      schema.push(getSchema('Readonly', { DataTypeName: 'boolean' }));
      schema.push(getSchema('Format'));
      schema.push(getSchema('Column Order', { DataTypeName: 'int' }));
      schema.push(getSchema('Lookup Code Field'));
      schema.push(getSchema('Lookup Type'));
      schema.push(getSchema('Lookup SP'));
      schema.push(getSchema('Lookup SP Parameter'));

      const booleanLookupList = [
        {
          lookup_code: 'null',
          lookup_code_desc: '',
        },
        {
          lookup_code: false,
          lookup_code_desc: 'No',
        },
        {
          lookup_code: true,
          lookup_code_desc: 'Yes',
        },
      ];

      const lookupTypeListResponse = await fetchData('CommonFunctions', 'CdosysLookupTypeList', MethodType.Post, {});
      const lookupTypes = lookupTypeListResponse.dataObject.data.map((record: any) => {
        return {
          lookup_code: record.lookup_type_id,
          lookup_code_desc: record.lookup_type,
        };
      });

      lookupDataList = [
        {
          ColumnName: 'Hidden',
          LookupList: [...booleanLookupList],
        },
        {
          ColumnName: 'Readonly',
          LookupList: [...booleanLookupList],
        },
        {
          ColumnName: 'Lookup Type',
          LookupList: [...lookupTypes],
        },
      ];
    } else {
      const dataListCopy = { ...dataList };
      schema = dataListCopy.schema;
      lookupDataList = dataListCopy.lookupDataList;
    }

    let data: any = [];
    Object.keys(columnData).forEach((colKey: any) => {
      data.push({
        ...columnData[colKey],
        Caption: columnData[colKey].columnCaption || colKey,
        'Column Name': colKey,
        'Column Order': columnData[colKey].columnOrder,
        'Data Type': columnData[colKey].dataType,
        Format: columnData[colKey].columnFormat,
        Hidden: columnData[colKey].isHidden,
        'Lookup Code Field': columnData[colKey].lookupCodeField,
        'Lookup SP': columnData[colKey].lookupSp,
        'Lookup SP Parameter': columnData[colKey].lookupSpParameter,
        'Lookup Type': columnData[colKey].lookupTypeId,
        Readonly: columnData[colKey].isReadonly,
        Width: columnData[colKey].width,
        columnId: columnData[colKey].columnId,
      });
    });

    data = data.sort((first: any, second: any) => {
      if (first.Hidden < second.Hidden) {
        return -1;
      } else if (first.Hidden === second.Hidden) {
        if (first.columnOrder < second.columnOrder) {
          return -1;
        } else {
          return 1;
        }
      } else {
        return 1;
      }
    });
    setDataList({ schema, data, lookupDataList });
    setGridLoading(false);
    setIncludedLocalStore(includedLocalStoreVal);
    setSpParameters(spParametersVal);
  };

  const getSchema = (columnName: string, extraProps: any = {}) => {
    return {
      BaseColumnName: columnName,
      Caption: columnName,
      ColumnName: columnName,
      ColumnSize: 50,
      DataField: columnName,
      DataTypeName: 'varchar',
      Format: undefined,
      Hidden: false,
      IsReadOnly: false,
      NumericPrecision: 255,
      NumericScale: 255,
      Width: undefined,
      ...extraProps,
    };
  };

  // Save the modified data
  const onSave = async (rowData: any) => {
    const params = {
      SpDataDictDetails: createsDataDictDetailString(rowData),
      SpName: spName,
      SpParams: spParameters,
      spId: spIdRef.current,
      userId: getUsername(),
    };
    const response = await saveData('ExpressionBuilder', 'SpSchemaPut', params);
    if (!response.hasError) {
      editedRowsRef.current = [];
      editedRowsObjRef.current = {};
      searchData(false);
    }
  };

  const onSaveAll = async () => {
    saveInlineChangesReqRef.current = false;

    await gridRef.current.instance().saveEditData();

    setIsLoading(true);

    let rowData = [...dataList.data];

    // Update the updated column order
    rowData = rowData.filter((row: any) => row.Hidden === false);
    rowData.forEach((row: any, index: number) => (row['Column Order'] = index));

    // update inline changes if any
    rowData.forEach((row: any) => {
      const key = row.dataField;
      if (editedRowsObjRef.current[key]) {
        const data = editedRowsObjRef.current[key];
        Object.keys(data).forEach((columnName: string) => {
          row[columnName] = data[columnName];
        });
      }
    });

    const params = {
      SpDataDictDetails: createsDataDictDetailString(rowData),
      SpName: spName,
      SpParams: spParameters,
      spId: spIdRef.current,
      userId: getUsername(),
    };
    const response = await saveData('ExpressionBuilder', 'SpSchemaPut', params);
    if (!response.hasError) {
      props.onSave();
    } else {
      setIsLoading(false);
    }
  };

  const onDeleteClicked = () => setShowDelete(true);

  const onModalClose = () => setShowDelete(false);

  // <column_id>;<column_name>;<data_type>;<column_caption>;
  // <column_format>;<column_width>;<column_order>;  --<is_hidden>;<is_readonly>;<lookup_code_field>
  // ;<lookup_type_id>;<lookup_sp>;<lookup_sp_parameter></lookup_sp_parameter>
  const createsDataDictDetailString = (sourceData: any) => {
    const spDataDictDetails = [];
    for (const data of sourceData) {
      const rowDetails = [];
      rowDetails.push(ConvertDouble(getTrimmedValue(data.columnId)));
      rowDetails.push(getTrimmedValue(data['Column Name']));
      rowDetails.push(getTrimmedValue(data['Data Type']));
      rowDetails.push(getTrimmedValue(data.Caption));
      rowDetails.push(getTrimmedValue(data.Format));
      rowDetails.push(getTrimmedValue(data.Width));
      rowDetails.push(getTrimmedValue(data['Column Order']));
      rowDetails.push(getTrimmedValue(data.Hidden));
      rowDetails.push(getTrimmedValue(data.Readonly));
      rowDetails.push(getTrimmedValue(data['Lookup Code Field']));
      rowDetails.push(getTrimmedValue(data['Lookup Type']));
      rowDetails.push(getTrimmedValue(data['Lookup SP']));
      rowDetails.push(getTrimmedValue(data['Lookup SP Parameter']));
      spDataDictDetails.push(rowDetails.join(';'));
    }
    return spDataDictDetails.join('|');
  };

  return (
    <EditModal
      reference={modalRef}
      isLoading={isLoading}
      isModalOpen={true}
      modalHeader={modalHeader}
      onCloseModal={props.onClose}
      onSave={onSaveAll}
      onDeleteClicked={onDeleteClicked}
      showDeleteButton={true}
    >
      <table style={{ width: 'fit-content' }}>
        <tbody>
          <TextboxControlRow
            label="Source from view source"
            value={source}
            name="source"
            isReadonly={true}
            isRequired={true}
            onChange={genericChangeHandler}
          />
          <TextboxControlRow
            label="Stored Procedure Name"
            value={spName}
            name="spName"
            isReadonly={true}
            isRequired={true}
            onChange={genericChangeHandler}
          />
          <TextboxControlRow
            label="Stored Procedure Parameters"
            value={spParameters}
            name="spParameters"
            isReadonly={false}
            isRequired={false}
            onChange={genericChangeHandler}
          />
        </tbody>
      </table>
      <br />
      {includedLocalStore && (
        <PageMessageControl
          iconType={PageMessageIconType.Information}
          label={
            'The grid includes changes you made locally. If you dont want to save these changes, please use reset icon to revert the changes'
          }
        />
      )}
      <GridControl
        gridTitle={'Grid Layout'}
        storageKey={'gridLayoutEdit'}
        repaintComponent={modalRef}
        isLoading={gridLoading}
        dataObject={dataList}
        theme={Theme.Dark}
        showPagination={false}
        editParams={editParamsRef.current}
        onReorder={onReorder}
        onEditorPreparing={onEditorPreparingHandler}
        dxDataGridProps={{ onRowValidating: onRowValidating }}
        reference={gridRef}
      />
      {showDelete && (
        <DeleteModal
          isDtoRequired={true}
          isModalOpen={showDelete}
          onCloseModal={onModalClose}
          onDeleteData={props.onClose}
          methodRoute={'/ExpressionBuilder/SpSchemaDel'}
          parameters={{ spId: spIdRef.current }}
          message={`Layout Details of ${spName}`}
        />
      )}
    </EditModal>
  );
};

export default GridLayoutEdit;
